
import api from "../api";
import ApiConstant from "../../constants/api.constants";
import {
  generateTestReportPdf,
  buildTestReportPdf,
} from "../../components/TestReport/TestReportPdf";
import {
  generateSampleReportPdf,
  buildSampleReportPdf,
  SampleGroup,
} from "../../components/TestReport/SampleReportPdf";

/** ---------- Shared DTOs ---------- */
export interface ListItemDTO {
  id: number;
  name: string;
}

export interface BatchOptionItem {
  batchId: number;
  batchCode: string;
  byProductId: number | null;
  byProductName: string | null;
  manufacturingUnitId: number | null;
  manufacturingUnitName: string | null;
  posId: number | null;
  availableStock?: number | null;
  productId?: number | null;
  productName?: string | null;
  subCategoryId?: number | null;
  subCategoryName?: string | null;
}

export interface TestResultDTO {
  testId: number;
  testName: string;
  value: number | null;
}

export interface TestReportSampleGroupDTO {
  sampleId: number;
  sampleName: string;
  results: TestResultDTO[];
}

export interface TestReportDTO {
  id: number;
  batchNo: string | number;
  productId: number;
  productName: string;
  sampleQty: number | null;
  testedDate: string | null;

  analysisStartDate?: string | null;
  analysisEndDate?: string | null;

  manufacturingUnitId?: number | null;
  testedByUserId?: number | null;

  manufacturingUnitName?: string | null;
  testedByName?: string | null;

  samples: TestReportSampleGroupDTO[];
}

export interface TestReportResultDTO {
  testId: number;
  testName: string;
  result: number;
  status: string | null;
}

export interface MUUserItem {
  id: number;
  name: string;
}

export interface ToleranceRow {
  testId: number;
  minTolerance: number | null;
  maxTolerance: number | null;
}

/** Mapping for chemicals & instruments per test. */
export type TestChemsInstrDTO = {
  testId: number;
  chemicals: string[];
  instruments: string[];
};

/* ===========================================================
   Helpers
   =========================================================== */
const toDateOnly = (d?: Date | null) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toHms = (d?: Date | null) => {
  if (!d) return null;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

const formatBatchCode = (batchNo?: string | number | null) => {
  const raw = String(batchNo ?? "").trim();
  if (!raw) return "";
  if (/^[Bb]\d+$/.test(raw)) return raw.toUpperCase();
  if (/^\d+$/.test(raw)) return `B${raw.padStart(8, "0")}`;
  return raw;
};

const normalizeBatchNoForApi = (batchNo?: string | number | null) => {
  if (batchNo == null) return null;
  if (typeof batchNo === "number") {
    return Number.isFinite(batchNo) ? batchNo : null;
  }

  const digits = String(batchNo).replace(/\D/g, "");
  if (!digits) return null;
  const parsed = Number(digits);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeBatchRows = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.batches)) return data.batches;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

// Build printable address lines from an M_POS row.
const buildMuAddressLines = (mu: any): string[] => {
  const lines: string[] = [];
  const push = (s?: any) => {
    const t = (s ?? "").toString().trim();
    if (t) lines.push(t);
  };

  push(mu?.address1 ?? mu?.Address1);

  const a2 = (mu?.address2 ?? mu?.Address2) as string | undefined;
  if (a2) {
    const chunks = a2.split(",").map((s) => s.trim()).filter(Boolean);
    if (chunks.length) chunks.forEach(push);
    else push(a2);
  }

  push(mu?.landmark ?? mu?.Landmark);

  const zip = (mu?.zipcode ?? mu?.Zipcode ?? "").toString().trim();
  if (zip && !lines.some((l) => l.includes(zip))) push(zip);

  return lines;
};

// Robust MU match: by id, then name, then code
const findMuRow = (mus: any[], id?: number | null, name?: string | null): any | undefined => {
  if (id != null) {
    const m = mus.find((x) => Number(x.id) === Number(id));
    if (m) return m;
  }
  const norm = (s: any) => (s ?? "").toString().trim().toLowerCase();

  if (name) {
    const n = norm(name);
    let m = mus.find((x) => norm(x.name ?? x.posName ?? x.PosName) === n);
    if (m) return m;

    m = mus.find((x) => norm(x.name ?? x.posName ?? x.PosName).includes(n));
    if (m) return m;

    m = mus.find((x) => norm(x.posCode ?? x.PosCode ?? x.code) === n);
    if (m) return m;
  }

  return undefined;
};

/* ===========================================================
   Lookups / Reads
   =========================================================== */
const getOilTypes = async (): Promise<ListItemDTO[]> => {
  const { data } = await api.get(ApiConstant.testReportOilTypes);
  return data ?? [];
};

const getManufacturingUnits = async (): Promise<any[]> => {
  const { data } = await api.get(ApiConstant.oilProdMUList);
  const rows = Array.isArray(data) ? data : [];
  return rows.map((r: any) => {
    const id = r.posID ?? r.posId ?? r.PosID ?? r.POSID ?? r.id ?? r.Id;
    const name = r.posName ?? r.PosName ?? r.POSName ?? r.name ?? r.Name;
    const posCode = r.posCode ?? r.PosCode ?? r.POSCode ?? r.code ?? r.Code;
    const address1 = r.address1 ?? r.Address1 ?? r.addr1 ?? r.Addr1;
    const address2 = r.address2 ?? r.Address2 ?? r.addr2 ?? r.Addr2;
    const zipcode = r.zipcode ?? r.Zipcode ?? r.pincode ?? r.PinCode;
    const landmark = r.landmark ?? r.Landmark;

    return {
      ...r,
      id: Number(id),
      name: String(name),
      posCode,
      address1,
      address2,
      zipcode,
      landmark,
    };
  });
};

const getOperatorsByManufacturingUnit = async (posId: number): Promise<MUUserItem[]> => {
  try {
    const testersPath = (ApiConstant as any).testReportOilTestersByPos as string | undefined;
    if (testersPath) {
      if (testersPath.includes("{posId}")) {
        const url = testersPath.replace("{posId}", String(posId));
        const { data } = await api.get(url);
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        return arr.map((u: any) => ({
          id: Number(u.id ?? u.Id),
          name: String(u.name ?? u.Name ?? "").trim(),
        }));
      } else {
        const { data } = await api.get(testersPath, { params: { posId } });
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        return arr.map((u: any) => ({
          id: Number(u.id ?? u.Id),
          name: String(u.name ?? u.Name ?? "").trim(),
        }));
      }
    }

    const { data } = await api.get(ApiConstant.oilProdOpsByPos, { params: { posId } });
    const ops: any[] = Array.isArray(data?.operators)
      ? data.operators
      : Array.isArray(data)
      ? data
      : [];
    const testers = ops.filter((o) => {
      const did = Number(o.designationId ?? o.DesignationID);
      const dname = String(o.designationName ?? o.DesignationName ?? "")
        .trim()
        .toLowerCase();
      return did === 20 || dname === "oil tester";
    });
    return testers.map((o) => ({
      id: Number(o.id ?? o.Id),
      name: String(o.name ?? o.Name ?? "").trim(),
    }));
  } catch {
    return [];
  }
};

const getOilTestersByManufacturingUnit = async (posId: number): Promise<MUUserItem[]> => {
  try {
    const path =
      (ApiConstant as any).testReportOilTestersByPos ||
      (ApiConstant as any).oilTestersByPos;

    if (path) {
      if (path.includes("{posId}")) {
        const url = path.replace("{posId}", String(posId));
        const { data } = await api.get(url);
        const arr = Array.isArray(data) ? data : (data?.testers ?? data?.operators ?? []);
        return (arr ?? []).map((u: any) => ({
          id: Number(u.id ?? u.Id),
          name: String(u.name ?? u.Name ?? "").trim(),
        }));
      } else {
        const { data } = await api.get(path, { params: { posId } });
        const arr = Array.isArray(data) ? data : (data?.testers ?? data?.operators ?? []);
        return (arr ?? []).map((u: any) => ({
          id: Number(u.id ?? u.Id),
          name: String(u.name ?? u.Name ?? "").trim(),
        }));
      }
    }

    const { data } = await api.get("/api/TestReport/oil-testers", { params: { posId } });
    const arr = Array.isArray(data) ? data : (data?.testers ?? data?.operators ?? []);
    return (arr ?? []).map((u: any) => ({
      id: Number(u.id ?? u.Id),
      name: String(u.name ?? u.Name ?? "").trim(),
    }));
  } catch {
    return [];
  }
};

const getOilTesters = async (): Promise<MUUserItem[]> => {
  try {
    const units = await getManufacturingUnits();
    const unitIds = Array.isArray(units)
      ? units
          .map((u: any) => Number(u.id ?? u.posID ?? u.posId))
          .filter((id: number) => Number.isFinite(id) && id > 0)
      : [];

    if (unitIds.length === 0) return [];

    const testerLists = await Promise.all(
      unitIds.map((posId) => getOilTestersByManufacturingUnit(posId))
    );

    const seen = new Set<number>();
    const merged: MUUserItem[] = [];

    testerLists.flat().forEach((tester) => {
      const id = Number(tester.id);
      const name = String(tester.name ?? "").trim();
      if (!Number.isFinite(id) || id <= 0 || !name || seen.has(id)) return;
      seen.add(id);
      merged.push({ id, name });
    });

    return merged.sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    return [];
  }
};

const getTemplateForProduct = async (productId: number): Promise<TestReportDTO | null> => {
  try {
    const { data } = await api.get<TestReportDTO>(ApiConstant.testReportTemplate, {
      params: { productId },
      headers: { Accept: "application/json" },
    });
    return data ?? null;
  } catch {
    return null;
  }
};

const getReportById = async (reportId: number): Promise<TestReportDTO> => {
  const path = (ApiConstant as any).testReportGetById as string | undefined;

  if (path) {
    if (path.includes("{reportId}")) {
      const url = path.replace("{reportId}", String(reportId));
      const { data } = await api.get(url);
      return data as TestReportDTO;
    } else {
      const { data } = await api.get(path, { params: { reportId } });
      return data as TestReportDTO;
    }
  }

  const { data } = await api.get(`/api/TestReport/${reportId}`);
  return data as TestReportDTO;
};

const calculateResultsPreviewForProduct = async (
  productId: number,
  values?: Record<string, number>
): Promise<TestReportResultDTO[]> => {
  const body = values ?? {};
  const { data } = await api.post(ApiConstant.testReportCalcPreviewByProduct, body, {
    params: { productId },
  });
  return data ?? [];
};

const calculateResultsPreviewForReport = async (
  reportId: number,
  values?: Record<string, number>
): Promise<TestReportResultDTO[]> => {
  const body = values ?? {};
  const path = (ApiConstant as any).testReportCalcPreviewByReport as string | undefined;

  if (path) {
    if (path.includes("{reportId}")) {
      const url = path.replace("{reportId}", String(reportId));
      const { data } = await api.post(url, body);
      return data ?? [];
    } else {
      const { data } = await api.post(path, body, { params: { reportId } });
      return data ?? [];
    }
  }
  throw new Error("No calculate-preview-by-report endpoint configured");
};

const getTolerances = async (productId: number): Promise<ToleranceRow[]> => {
  const { data } = await api.get(ApiConstant.testReportTolerances, { params: { productId } });
  return Array.isArray(data) ? data : [];
};

/* ===========================================================
   Chems/Instruments lookup
   =========================================================== */
const getChemsInstrForTestIds = async (
  testIds: number[]
): Promise<Record<number, TestChemsInstrDTO>> => {
  const map: Record<number, TestChemsInstrDTO> = {};
  if (!Array.isArray(testIds) || testIds.length === 0) return map;

  const path =
    (ApiConstant as any).testReportChemsInstrLookup ||
    (ApiConstant as any).testChemsInstrByTestIds;

  if (!path) return map;

  const { data } = await api.post(path, { testIds });
  const arr: any[] = Array.isArray(data) ? data : data?.items ?? [];

  for (const row of arr) {
    const tid = Number(row.testId ?? row.TestId ?? row.TestID);
    if (!Number.isFinite(tid)) continue;
    const chems = Array.isArray(row.chemicals)
      ? row.chemicals
      : Array.isArray(row.chemicalList)
      ? row.chemicalList
      : [];
    const instr = Array.isArray(row.instruments)
      ? row.instruments
      : Array.isArray(row.instrumentList)
      ? row.instrumentList
      : [];
    map[tid] = {
      testId: tid,
      chemicals: chems
        .map((c: any) => String(c?.name ?? c?.Name ?? c).trim())
        .filter(Boolean),
      instruments: instr
        .map((i: any) => String(i?.name ?? i?.Name ?? i).trim())
        .filter(Boolean),
    };
  }

  return map;
};

/* ===========================================================
   Search
   =========================================================== */
export interface SearchReportsRequest {
  reportDate?: string;
  posId?: number;
  testerId?: number;
  productId?: number;
  batchNo?: string | number;
}

export interface SearchReportsRow {
  id: number;
  reportDate: string | null;
  productName: string;
  batchNo: string | number;
  posName?: string;
  testerName?: string;
}

const searchReports = async (params: SearchReportsRequest): Promise<SearchReportsRow[]> => {
  const { batchNo, ...rest } = params;
  const apiParams = {
    ...rest,
    ...(normalizeBatchNoForApi(batchNo) != null ? { batchNo: normalizeBatchNoForApi(batchNo) } : {}),
  };

  const { data } = await api.get(ApiConstant.testReportSearch, { params: apiParams });
  return Array.isArray(data) ? data : [];
};

/* ===========================================================
   Paged list
   =========================================================== */
export type TestReportListFilter = {
  byproductId?: number;
  batchNo?: string | number;
  posId?: number;
  testerId?: number;
  manufacturingUnitId?: number;
  testedByUserId?: number;
  testedStartDate?: string;
  testedEndDate?: string;
  keyword?: string;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  pageNumber?: number;
  pageSize?: number;
};

export interface TestReportListRow {
  reportId: number;
  reportDate?: string | null;
  testedDate?: string | null;
  oilName: string;
  manufacturingUnit: string;
  testedByName: string;
  batchNo: string | number;
  isDeleted: boolean;
  batchCode: string ;
}
export interface TestReportListRow {
  reportId: number;
  reportDate?: string | null;
  testedDate?: string | null;
  oilName: string;
  manufacturingUnitId?: number | null; // <--- add this
  testedByUserId?: number | null;      // <--- add this
  manufacturingUnit: string;
  testedByName: string;
  batchNo: string | number;
  isDeleted: boolean;
}

export type PaginatedTestReportList = {
  totalRecords: number;
  items: TestReportListRow[];
};

const getTestReports = async (
  filter: TestReportListFilter
): Promise<PaginatedTestReportList> => {
  const payload: any = { ...filter };

  const normalizedBatchNo = normalizeBatchNoForApi(payload.batchNo);
  if (normalizedBatchNo != null) {
    payload.batchNo = normalizedBatchNo;
  } else {
    delete payload.batchNo;
  }

  // Map posId → manufacturingUnitId
  if (payload.manufacturingUnitId == null && payload.posId != null) {
    payload.manufacturingUnitId = payload.posId;
  }
  delete payload.posId;

  // Map testerId → testedByUserId
  if (payload.testedByUserId == null && payload.testerId != null) {
    payload.testedByUserId = payload.testerId;
  }
  delete payload.testerId;

  // Remove empty/undefined keys
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined || payload[k] === null || payload[k] === "") {
      delete payload[k];
    }
  });

  // Fetch reports from API
  const { data } = await api.post(ApiConstant.testReportGetList, payload, {
    headers: { "Content-Type": "application/json" },
  });

  const pagedList = (data ?? { totalRecords: 0, items: [] }) as PaginatedTestReportList;

  // Filter out deleted rows BEFORE mapping MU/tester names
  const activeItems = pagedList.items.filter((r) => !r.isDeleted);

  // Fetch all manufacturing units
  const mus = await getManufacturingUnits();

  // Map MU and tester names
  const itemsWithMU: TestReportListRow[] = activeItems.map((r) => {
    const mu = mus.find((m) => Number(m.id) === Number(r.manufacturingUnitId ?? 0));
    const muName = mu ? mu.name : "";

    return {
      ...r,
      manufacturingUnit: muName || r.manufacturingUnit || "",
      testedByName: r.testedByName ?? "",
    };
  });

  const rawServerTotal = pagedList && typeof pagedList.totalRecords !== "undefined" ? Number(pagedList.totalRecords) : NaN;
  const serverTotal = Number.isFinite(rawServerTotal) ? rawServerTotal : null;
  const totalToReturn = serverTotal !== null ? serverTotal : itemsWithMU.length;

  return {
    totalRecords: totalToReturn,
    items: itemsWithMU,
  };
};

const existsReportForProductBatch = async (
  productId: number,
  batchNo: string | number,
  excludeReportId?: number | null
): Promise<boolean> => {
  if (!Number.isFinite(productId) || String(batchNo ?? "").trim() === "") return false;

  try {
    const rows = await searchReports({ productId, batchNo });
    if (Array.isArray(rows) && rows.length > 0) {
      if (excludeReportId != null) {
        const other = rows.some((r: any) => Number(r.id) !== Number(excludeReportId));
        if (other) return true;
      } else {
        return true;
      }
    }
  } catch {
  }

  try {
    const resp = await getTestReports({
      byproductId: productId,
      batchNo,
      pageNumber: 1,
      pageSize: 5,
    });
    const items = Array.isArray(resp?.items) ? resp.items : [];
    if (excludeReportId != null) {
      return items.some((r) => Number(r.reportId) !== Number(excludeReportId));
    }
    return items.length > 0;
  } catch {
    return false;
  }
};


export interface UpsertRequest {
  reportId?: number | null;
  productId: number;

  batchId?: number | null; // ✅ dropdown
  batchNo?: string | number | null; // ✅ manual

  sampleQty?: number | null;
  testedDate?: Date | null;
  analysisStart?: Date | null;
  analysisEnd?: Date | null;
  manufacturingUnitId?: number | null;
  testedByUserId?: number | null;
  inputs: Record<string, number>;
}


export interface UpsertResponse {
  report: TestReportDTO;
  results: TestReportResultDTO[];
}

const upsertReportWithResults = async (req: UpsertRequest): Promise<UpsertResponse> => {
  const body: any = {
  reportId: req.reportId ?? null,
  productId: req.productId,

  batchId: req.batchId ?? null,
  batchNo: normalizeBatchNoForApi(req.batchNo),

  sampleQty: req.sampleQty ?? null,
  testedDate: toDateOnly(req.testedDate),
  analysisStartTime: toHms(req.analysisStart),
  analysisEndTime: toHms(req.analysisEnd),
  inputs: req.inputs ?? {},
  manufacturingUnitId: req.manufacturingUnitId ?? null,
  testedByUserId: req.testedByUserId ?? null,
};

  const { data } = await api.post(ApiConstant.testReportUpsert, body, {
    headers: { "Content-Type": "application/json" },
  });
  return data as UpsertResponse;
};

/* ===========================================================
   PDF helpers (TEST REPORT - grid)
   =========================================================== */
export type PdfOptions = {
  reportDate?: string | Date | null;
  companyLines?: string[];
  centerLogoUrl?: string;
  qaSealUrl?: string;
  filename?: string;
  overrides?: Record<string, number>;
};

const generatePdfForReportId = async (reportId: number, opts: PdfOptions = {}) => {
  const report = await getReportById(reportId);

  let companyLines = opts.companyLines;
  let muDisplayName: string | null | undefined = report.manufacturingUnitName ?? null;

  try {
    const mus = await getManufacturingUnits();
    const mu = findMuRow(
      mus,
      report.manufacturingUnitId ?? null,
      report.manufacturingUnitName ?? null
    );

    if (mu) {
      const addrLines = buildMuAddressLines(mu);
      if (!companyLines || companyLines.length === 0) {
        companyLines = addrLines;
      }
      const nameCandidate = mu.name ?? mu.posName ?? mu.PosName ?? null;
      muDisplayName = nameCandidate || mu.posCode || mu.PosCode || mu.code || null;
    }
  } catch {
    /* ignore */
  }

  const inputsFromReport: Record<string, number> = {};
  (report.samples ?? []).forEach((sg) =>
    (sg.results ?? []).forEach((r) => {
      const n = Number(r.value);
      if (Number.isFinite(n)) inputsFromReport[`${r.testId}:${sg.sampleId}`] = n;
    })
  );
  const effectiveOverrides = { ...inputsFromReport, ...(opts.overrides ?? {}) };

  let results: TestReportResultDTO[] = [];
  try {
    results = await calculateResultsPreviewForReport(report.id, effectiveOverrides);
  } catch {
    results = await calculateResultsPreviewForProduct(report.productId, effectiveOverrides);
  }

  const tolRows = await getTolerances(report.productId);
  const tolMap = new Map<number, { min: number | null; max: number | null }>();
  (tolRows ?? []).forEach((r) =>
    tolMap.set(r.testId, { min: r.minTolerance, max: r.maxTolerance })
  );

  const meta = {
    reportId: report.id,
    reportDate: opts.reportDate ?? null,
    manufacturingUnitId: report.manufacturingUnitId ?? null,
    manufacturingUnitName: muDisplayName ?? null,
    testedDate: report.testedDate ?? null,
    analysisStart: report.analysisStartDate ?? null,
    analysisEnd: report.analysisEndDate ?? null,
    sampleQty: report.sampleQty ?? null,
    testerName: report.testedByName ?? null,
    oilName: report.productName ?? null,
    batchName: String(report.batchNo ?? ""),
  };

  await generateTestReportPdf({
    meta,
    results,
    tolerances: tolMap,
    centerLogoUrl: opts.centerLogoUrl,
    qaSealUrl: opts.qaSealUrl,
    filename: opts.filename ?? `TestReport_${report.batchNo}_${report.productName}.pdf`,
  });
};

/** Build the TEST REPORT and return a Blob URL (in-app view). */
const generatePdfUrlForReportId = async (
  reportId: number,
  opts: PdfOptions = {}
): Promise<string> => {
  const report = await getReportById(reportId);

  let companyLines = opts.companyLines;
  let muDisplayName: string | null | undefined = report.manufacturingUnitName ?? null;

  try {
    const mus = await getManufacturingUnits();
    const mu = findMuRow(
      mus,
      report.manufacturingUnitId ?? null,
      report.manufacturingUnitName ?? null
    );

    if (mu) {
      const addrLines = buildMuAddressLines(mu);
      if (!companyLines || companyLines.length === 0) {
        companyLines = addrLines;
      }
      const nameCandidate = mu.name ?? mu.posName ?? mu.PosName ?? null;
      muDisplayName = nameCandidate || mu.posCode || mu.PosCode || mu.code || null;
    }
  } catch {}

  const inputsFromReport: Record<string, number> = {};
  (report.samples ?? []).forEach((sg) =>
    (sg.results ?? []).forEach((r) => {
      const n = Number(r.value);
      if (Number.isFinite(n)) inputsFromReport[`${r.testId}:${sg.sampleId}`] = n;
    })
  );
  const effectiveOverrides = { ...inputsFromReport, ...(opts.overrides ?? {}) };

  let results: TestReportResultDTO[] = [];
  try {
    results = await calculateResultsPreviewForReport(report.id, effectiveOverrides);
  } catch {
    results = await calculateResultsPreviewForProduct(report.productId, effectiveOverrides);
  }

  const tolRows = await getTolerances(report.productId);
  const tolMap = new Map<number, { min: number | null; max: number | null }>();
  (tolRows ?? []).forEach((r) =>
    tolMap.set(r.testId, { min: r.minTolerance, max: r.maxTolerance })
  );

  const meta = {
    reportId: report.id,
    reportDate: opts.reportDate ?? null,
    manufacturingUnitId: report.manufacturingUnitId ?? null,
    manufacturingUnitName: muDisplayName ?? null,
    testedDate: report.testedDate ?? null,
    analysisStart: report.analysisStartDate ?? null,
    analysisEnd: report.analysisEndDate ?? null,
    sampleQty: report.sampleQty ?? null,
    testerName: report.testedByName ?? null,
    oilName: report.productName ?? null,
    batchName: String(report.batchNo ?? ""),
  };

  const doc = await buildTestReportPdf({
    meta,
    results,
    tolerances: tolMap,
    centerLogoUrl: opts.centerLogoUrl,
    qaSealUrl: opts.qaSealUrl,
  });

  const blob = doc.output("blob");
  return URL.createObjectURL(blob);
};

/* ===== Helpers for VIEW + SAVE with proper filename ===== */
type ViewerPayload = { url: string; doc: any; filename: string };

const getTestReportViewerPayload = async (
  reportId: number,
  opts: PdfOptions = {}
): Promise<ViewerPayload> => {
  const report = await getReportById(reportId);

  let companyLines = opts.companyLines;
  let muDisplayName: string | null | undefined = report.manufacturingUnitName ?? null;
  try {
    const mus = await getManufacturingUnits();
    const mu = findMuRow(
      mus,
      report.manufacturingUnitId ?? null,
      report.manufacturingUnitName ?? null
    );
    if (mu) {
      const addrLines = buildMuAddressLines(mu);
      if (!companyLines || companyLines.length === 0) companyLines = addrLines;
      const nameCandidate = mu.name ?? mu.posName ?? mu.PosName ?? null;
      muDisplayName = nameCandidate || mu.posCode || mu.PosCode || mu.code || null;
    }
  } catch {}

  const inputsFromReport: Record<string, number> = {};
  (report.samples ?? []).forEach((sg) =>
    (sg.results ?? []).forEach((r) => {
      const n = Number(r.value);
      if (Number.isFinite(n)) inputsFromReport[`${r.testId}:${sg.sampleId}`] = n;
    })
  );

  let results: TestReportResultDTO[] = [];
  try {
    results = await calculateResultsPreviewForReport(report.id, inputsFromReport);
  } catch {
    results = await calculateResultsPreviewForProduct(report.productId, inputsFromReport);
  }

  const tolRows = await getTolerances(report.productId);
  const tolMap = new Map<number, { min: number | null; max: number | null }>();
  (tolRows ?? []).forEach((r) =>
    tolMap.set(r.testId, { min: r.minTolerance, max: r.maxTolerance })
  );

  const meta = {
    reportId: report.id,
    reportDate: opts.reportDate ?? null,
    manufacturingUnitId: report.manufacturingUnitId ?? null,
    manufacturingUnitName: muDisplayName ?? null,
    testedDate: report.testedDate ?? null,
    analysisStart: report.analysisStartDate ?? null,
    analysisEnd: report.analysisEndDate ?? null,
    sampleQty: report.sampleQty ?? null,
    testerName: report.testedByName ?? null,
    oilName: report.productName ?? null,
    batchName: formatBatchCode(report.batchNo),
  };

  const doc = await buildTestReportPdf({
    meta,
    results,
    tolerances: tolMap,
    centerLogoUrl: opts.centerLogoUrl,
    qaSealUrl: opts.qaSealUrl,
  });

  const filename =
    opts.filename ?? `TestReport_${report.batchNo}_${report.productName}.pdf`;
  (doc as any).setProperties?.({ title: filename });
  const url = URL.createObjectURL(doc.output("blob"));
  return { url, doc, filename };
};

/* ===========================================================
   SAMPLE REPORT — includes chems/instruments (no durations)
   =========================================================== */
const generateSampleReportForReportId = async (
  reportId: number,
  opts: PdfOptions = {}
) => {
  const report = await getReportById(reportId);

  let companyLines = opts.companyLines;
  let muDisplayName: string | null | undefined = report.manufacturingUnitName ?? null;
  try {
    const mus = await getManufacturingUnits();
    const mu = findMuRow(
      mus,
      report.manufacturingUnitId ?? null,
      report.manufacturingUnitName ?? null
    );
    if (mu) {
      const addrLines = buildMuAddressLines(mu);
      if (!companyLines || companyLines.length === 0) companyLines = addrLines;
      const nameCandidate = mu.name ?? mu.posName ?? mu.PosName ?? null;
      muDisplayName = nameCandidate || mu.posCode || mu.PosCode || mu.code || null;
    }
  } catch {
    /* ignore */
  }

  // Group by test (Sample Name/Value)
  const byTest: Record<
    number,
    {
      testId: number;
      testName: string;
      items: Array<{ label: string; value: number | string | null }>;
    }
  > = {};
  (report.samples ?? []).forEach((sg) => {
    (sg.results ?? []).forEach((r) => {
      if (!byTest[r.testId])
        byTest[r.testId] = { testId: r.testId, testName: r.testName, items: [] };
      byTest[r.testId].items.push({
        label: sg.sampleName,
        value: r.value == null ? "—" : r.value,
      });
    });
  });

  // Lookups (chems/instruments)
  const testIds = Object.keys(byTest).map(Number);
  const ciMap = await getChemsInstrForTestIds(testIds);

  // Build groups with chems/instruments (no duration)
  const groups: SampleGroup[] = Object.values(byTest)
    .sort((a, b) => a.testName.localeCompare(b.testName))
    .map((g) => {
      const ci = ciMap[g.testId];
      return {
        testId: g.testId,
        testName: g.testName,
        items: g.items,
        chemicals: ci ? ci.chemicals : [],
        instruments: ci ? ci.instruments : [],
      };
    });

  // Build results (per test) for Sample Report extra columns
  const inputsFromReport: Record<string, number> = {};
  (report.samples ?? []).forEach((sg) =>
    (sg.results ?? []).forEach((r) => {
      const n = Number(r.value);
      if (Number.isFinite(n)) inputsFromReport[`${r.testId}:${sg.sampleId}`] = n;
    })
  );
  let results: TestReportResultDTO[] = [];
  try {
    results = await calculateResultsPreviewForReport(report.id, inputsFromReport);
  } catch {
    results = await calculateResultsPreviewForProduct(report.productId, inputsFromReport);
  }

  // Tolerances map
  const tolRows = await getTolerances(report.productId);
  const tolMap = new Map<number, { min: number | null; max: number | null }>();
  (tolRows ?? []).forEach((r) =>
    tolMap.set(r.testId, { min: r.minTolerance, max: r.maxTolerance })
  );

  const meta = {
    reportId: report.id,
    reportDate: opts.reportDate ?? null,
    manufacturingUnitId: report.manufacturingUnitId ?? null,
    manufacturingUnitName: muDisplayName ?? null,
    testedDate: report.testedDate ?? null,
    analysisStart: report.analysisStartDate ?? null,
    analysisEnd: report.analysisEndDate ?? null,
    sampleQty: report.sampleQty ?? null,
    testerName: report.testedByName ?? null,
    oilName: report.productName ?? null,
    batchName: String(report.batchNo ?? ""),
  };

  await generateSampleReportPdf({
    meta,
    groups,
    centerLogoUrl: opts.centerLogoUrl,
    qaSealUrl: opts.qaSealUrl,
    filename: `SampleReport_${report.batchNo}_${report.productName}.pdf`,
    results,
    tolerances: tolMap,
  });
};

const generateSamplePdfUrlForReportId = async (
  reportId: number,
  opts: PdfOptions = {}
) => {
  const report = await getReportById(reportId);

  let companyLines = opts.companyLines;
  let muDisplayName: string | null | undefined = report.manufacturingUnitName ?? null;
  try {
    const mus = await getManufacturingUnits();
    const mu = findMuRow(
      mus,
      report.manufacturingUnitId ?? null,
      report.manufacturingUnitName ?? null
    );
    if (mu) {
      const addrLines = buildMuAddressLines(mu);
      if (!companyLines || companyLines.length === 0) companyLines = addrLines;
      const nameCandidate = mu.name ?? mu.posName ?? mu.PosName ?? null;
      muDisplayName = nameCandidate || mu.posCode || mu.PosCode || mu.code || null;
    }
  } catch {}

  const byTest: Record<
    number,
    {
      testId: number;
      testName: string;
      items: Array<{ label: string; value: number | string | null }>;
    }
  > = {};
  (report.samples ?? []).forEach((sg) => {
    (sg.results ?? []).forEach((r) => {
      if (!byTest[r.testId])
        byTest[r.testId] = { testId: r.testId, testName: r.testName, items: [] };
      byTest[r.testId].items.push({
        label: sg.sampleName,
        value: r.value == null ? "—" : r.value,
      });
    });
  });

  const testIds = Object.keys(byTest).map(Number);
  const ciMap = await getChemsInstrForTestIds(testIds);

  const groups: SampleGroup[] = Object.values(byTest)
    .sort((a, b) => a.testName.localeCompare(b.testName))
    .map((g) => {
      const ci = ciMap[g.testId];
      return {
        testId: g.testId,
        testName: g.testName,
        items: g.items,
        chemicals: ci ? ci.chemicals : [],
        instruments: ci ? ci.instruments : [],
      };
    });

  const inputsFromReport: Record<string, number> = {};
  (report.samples ?? []).forEach((sg) =>
    (sg.results ?? []).forEach((r) => {
      const n = Number(r.value);
      if (Number.isFinite(n)) inputsFromReport[`${r.testId}:${sg.sampleId}`] = n;
    })
  );

  let results: TestReportResultDTO[] = [];
  try {
    results = await calculateResultsPreviewForReport(report.id, inputsFromReport);
  } catch {
    results = await calculateResultsPreviewForProduct(report.productId, inputsFromReport);
  }

  const tolRows = await getTolerances(report.productId);
  const tolMap = new Map<number, { min: number | null; max: number | null }>();
  (tolRows ?? []).forEach((r) =>
    tolMap.set(r.testId, { min: r.minTolerance, max: r.maxTolerance })
  );

  const meta = {
    reportId: report.id,
    reportDate: opts.reportDate ?? null,
    manufacturingUnitName: muDisplayName ?? null,
    testedDate: report.testedDate ?? null,
    analysisStart: report.analysisStartDate ?? null,
    analysisEnd: report.analysisEndDate ?? null,
    sampleQty: report.sampleQty ?? null,
    testerName: report.testedByName ?? null,
    oilName: report.productName ?? null,
    batchName: String(report.batchNo ?? ""),
    companyLines,
  };

  const doc = await buildSampleReportPdf({
    meta,
    groups,
    centerLogoUrl: opts.centerLogoUrl,
    qaSealUrl: opts.qaSealUrl,
    results,
    tolerances: tolMap,
  });

  const blob = doc.output("blob");
  return URL.createObjectURL(blob);
};

const getSampleReportViewerPayload = async (
  reportId: number,
  opts: PdfOptions = {}
) => {
  const report = await getReportById(reportId);

  let companyLines = opts.companyLines;
  let muDisplayName: string | null | undefined = report.manufacturingUnitName ?? null;
  try {
    const mus = await getManufacturingUnits();
    const mu = findMuRow(
      mus,
      report.manufacturingUnitId ?? null,
      report.manufacturingUnitName ?? null
    );
    if (mu) {
      const addrLines = buildMuAddressLines(mu);
      if (!companyLines || companyLines.length === 0) companyLines = addrLines;
      const nameCandidate = mu.name ?? mu.posName ?? mu.PosName ?? null;
      muDisplayName = nameCandidate || mu.posCode || mu.PosCode || mu.code || null;
    }
  } catch {}

  const byTest: Record<
    number,
    {
      testId: number;
      testName: string;
      items: Array<{ label: string; value: number | string | null }>;
    }
  > = {};
  (report.samples ?? []).forEach((sg) => {
    (sg.results ?? []).forEach((r) => {
      if (!byTest[r.testId])
        byTest[r.testId] = { testId: r.testId, testName: r.testName, items: [] };
      byTest[r.testId].items.push({
        label: sg.sampleName,
        value: r.value == null ? "—" : r.value,
      });
    });
  });

  const testIds = Object.keys(byTest).map(Number);
  const ciMap = await getChemsInstrForTestIds(testIds);

  const groups: SampleGroup[] = Object.values(byTest)
    .sort((a, b) => a.testName.localeCompare(b.testName))
    .map((g) => {
      const ci = ciMap[g.testId];
      return {
        testId: g.testId,
        testName: g.testName,
        items: g.items,
        chemicals: ci ? ci.chemicals : [],
        instruments: ci ? ci.instruments : [],
      };
    });

  const inputsFromReport: Record<string, number> = {};
  (report.samples ?? []).forEach((sg) =>
    (sg.results ?? []).forEach((r) => {
      const n = Number(r.value);
      if (Number.isFinite(n)) inputsFromReport[`${r.testId}:${sg.sampleId}`] = n;
    })
  );

  let results: TestReportResultDTO[] = [];
  try {
    results = await calculateResultsPreviewForReport(report.id, inputsFromReport);
  } catch {
    results = await calculateResultsPreviewForProduct(report.productId, inputsFromReport);
  }

  const tolRows = await getTolerances(report.productId);
  const tolMap = new Map<number, { min: number | null; max: number | null }>();
  (tolRows ?? []).forEach((r) =>
    tolMap.set(r.testId, { min: r.minTolerance, max: r.maxTolerance })
  );

  const meta = {
    reportId: report.id,
    reportDate: opts.reportDate ?? null,
    manufacturingUnitName: muDisplayName ?? null,
    testedDate: report.testedDate ?? null,
    analysisStart: report.analysisStartDate ?? null,
    analysisEnd: report.analysisEndDate ?? null,
    sampleQty: report.sampleQty ?? null,
    testerName: report.testedByName ?? null,
    oilName: report.productName ?? null,
    batchName: formatBatchCode(report.batchNo),
    companyLines,
  };

  const doc = await buildSampleReportPdf({
    meta,
    groups,
    centerLogoUrl: opts.centerLogoUrl,
    qaSealUrl: opts.qaSealUrl,
    results,
    tolerances: tolMap,
  });

  const filename =
    opts.filename ?? `SampleReport_${report.batchNo}_${report.productName}.pdf`;
  (doc as any).setProperties?.({ title: filename });
  const url = URL.createObjectURL(doc.output("blob"));
  return { url, doc, filename };
};

/* ===========================================================
   Delete
   =========================================================== */
const deleteReport = async (reportId: number): Promise<void> => {
  const path = (ApiConstant as any).testReportDelete as string | undefined;

  if (path) {
    if (path.includes("{reportId}")) {
      const url = path.replace("{reportId}", String(reportId));
      await api.delete(url);
    } else {
      await api.delete(path, { params: { reportId } });
    }
    return;
  }

  await api.delete(`/api/TestReport/${reportId}`);
};

const getBatches = async (
  keyword: string | null = null
): Promise<BatchOptionItem[]> => {
  try {
    const payload = { keyword: keyword ?? "" };

    const response = await api.post(
      ApiConstant.getBatches,
      payload,
      { headers: { "Content-Type": "application/json" } }
    );

    const raw = normalizeBatchRows(response.data);
    return raw.map((b: any): BatchOptionItem => ({
      batchId: Number(b.batchId ?? b.id ?? b.Id ?? b.BatchId),
      batchCode: String(b.batchCode ?? b.BatchCode ?? b.batchNo ?? ""),
      byProductId:
        b.byProductId ??
        b.ByProductId ??
        b.byproductId ??
        b.byProduct?.id ??
        b.productId ??
        b.ProductId ??
        null,
      byProductName:
        b.byProductName ??
        b.ByProductName ??
        b.byproductName ??
        b.byProduct?.name ??
        b.productName ??
        b.ProductName ??
        null,
      manufacturingUnitId:
        Number(b.manufacturingUnitPosId ?? b.posId ?? b.manufacturingUnitId ?? b.PosId ?? 0) ||
        null,
      manufacturingUnitName: String(
        b.manufacturingUnitName ??
          b.manufacturingUnitAddress ??
          b.posName ??
          b.PosName ??
          ""
      ).trim() || null,
      posId: Number(b.posId ?? b.PosId ?? b.manufacturingUnitPosId ?? 0) || null,
      availableStock: b.availableStock ?? b.AvailableStock ?? null,
      productId: b.productId ?? b.ProductId ?? null,
      productName: b.productName ?? b.ProductName ?? null,
      subCategoryId: b.subCategoryId ?? b.SubCategoryId ?? null,
      subCategoryName: b.subCategoryName ?? b.SubCategoryName ?? null,
    }));
  } catch (error) {
    console.error("Error fetching batches:", error);
    return [];
  }
};

const getBatchesByPosAndProduct = async (
  posId: number | null,
  productId: number,
  keyword: string = ""
): Promise<BatchOptionItem[]> => {
  try {
    const response = await api.post(
      ApiConstant.getBatchesByPosAndProduct,
      { posId, productId, keyword },
      { headers: { "Content-Type": "application/json" } }
    );

    const raw = normalizeBatchRows(response.data);
    return raw.map((b: any): BatchOptionItem => ({
      batchId: Number(b.batchId ?? b.id ?? b.Id ?? b.BatchId),
      batchCode: String(b.batchCode ?? b.BatchCode ?? b.batchNo ?? ""),
      byProductId:
        b.byProductId ??
        b.ByProductId ??
        b.byproductId ??
        b.byProduct?.id ??
        b.productId ??
        b.ProductId ??
        null,
      byProductName:
        b.byProductName ??
        b.ByProductName ??
        b.byproductName ??
        b.byProduct?.name ??
        b.productName ??
        b.ProductName ??
        null,
      manufacturingUnitId:
        Number(b.manufacturingUnitPosId ?? b.posId ?? b.manufacturingUnitId ?? b.PosId ?? 0) ||
        null,
      manufacturingUnitName: String(
        b.manufacturingUnitName ??
          b.manufacturingUnitAddress ??
          b.posName ??
          b.PosName ??
          ""
      ).trim() || null,
      posId: Number(b.posId ?? b.PosId ?? b.manufacturingUnitPosId ?? 0) || null,
      availableStock: b.availableStock ?? b.AvailableStock ?? null,
      productId: b.productId ?? b.ProductId ?? null,
      productName: b.productName ?? b.ProductName ?? null,
      subCategoryId: b.subCategoryId ?? b.SubCategoryId ?? null,
      subCategoryName: b.subCategoryName ?? b.SubCategoryName ?? null,
    }));
  } catch (error) {
    console.error("Error fetching batches:", error);
    return [];
  }
};


/* ===========================================================
   QR-BASED PDF DOWNLOAD (Styled Frontend Template)
   =========================================================== */


const getPublicReportByBatch = async (batchNo: string | number) => {
  const { data } = await api.get(
    `/api/TestReport/public/batch/${batchNo}`
  );
  return data;
};
/* ===========================================================
   Export
   =========================================================== */
const TestReportService = {
  getOilTypes,
  getManufacturingUnits,
  getOperatorsByManufacturingUnit,
  getOilTestersByManufacturingUnit,
  getOilTesters,

  getTemplateForProduct,
  getReportById,
  calculateResultsPreviewForProduct,
  calculateResultsPreviewForReport,
  getTolerances,
  searchReports,
  getTestReports,
  existsReportForProductBatch,
  upsertReportWithResults,
  getChemsInstrForTestIds,
  generatePdfForReportId,
  generatePdfUrlForReportId,
  getTestReportViewerPayload,
  generateSampleReportForReportId,
  generateSamplePdfUrlForReportId,
  getSampleReportViewerPayload,
  deleteReport,
  getBatches,
  getPublicReportByBatch,
  getBatchesByPosAndProduct
};

export default TestReportService;
